import React from 'react';
import AudioPlayer from './AudioPlayer';
import './programme.css';

const verseText = (v) => (typeof v === 'string' ? v : v?.text || '');
const unavailable = "Words not available here. Use the host's own text, or leave this hymn without words.";

const Rule = () => <div className="prog-rule" aria-hidden="true">❦</div>;

const Page = ({ children, className = '' }) => (
  <section className={`prog-page prog-paper bg-[#fbf8f1] border border-brand-dark rounded-2xl shadow-sm p-6 md:p-10 mb-6 ${className}`}>
    {children}
  </section>
);

const Cover = ({ programme }) => (
  <Page className="prog-cover">
    <div className="text-center prog-serif py-6">
      {programme.coverImage && (
        <img src={programme.coverImage} alt="" className="w-40 h-40 object-cover rounded-full mx-auto mb-6 border-4 border-white shadow" />
      )}
      <p className="uppercase tracking-[0.3em] text-xs text-text-muted mb-3">{programme.title || 'Order of Service'}</p>
      <h1 className="text-3xl md:text-4xl font-bold text-accent-navy">{programme.deceasedName || 'In Loving Memory'}</h1>
      <Rule />
      {programme.dates && <p className="text-lg text-text-main">{programme.dates}</p>}
      {programme.subtitle && <p className="italic text-text-muted mt-2">{programme.subtitle}</p>}
      {programme.details && <p className="whitespace-pre-line text-sm text-text-muted mt-6">{programme.details}</p>}
    </div>
  </Page>
);

const HymnBody = ({ item, large }) => {
  const chosen = item.versesChosen?.length ? item.versesChosen : null;
  const body = size => `${size} leading-relaxed text-text-main`;
  const cls = large ? body('text-2xl') : body('text-lg');

  if (item.hostOwnText) return null;
  if (Array.isArray(item.lyrics?.verses)) {
    return (
      <div className="mt-4 space-y-4">
        {item.lyrics.verses.map((v, i) => (chosen && !chosen.includes(i) ? null : (
          <div key={i} className="flex gap-3">
            <span className="text-accent-gold font-bold prog-serif w-6 text-right shrink-0">{i + 1}</span>
            <p className={`${cls} whitespace-pre-line`}>{verseText(v)}</p>
          </div>
        )))}
      </div>
    );
  }
  return <p className="text-sm italic text-text-muted mt-3">{item.lyrics?.reason || unavailable}</p>;
};

const ItemPage = ({ item, ctx }) => {
  const { pieceMap, large, showAudio } = ctx;
  const piece = pieceMap[item.pieceId];
  const isHymn = item.type === 'hymn';
  const src = showAudio && item.audio?.url;
  return (
    <Page>
      <div className="prog-serif">
        <p className="uppercase tracking-[0.25em] text-[10px] text-text-muted">{item.type}</p>
        <h2 className={`${large ? 'text-3xl' : 'text-2xl'} font-bold text-accent-navy mt-1`}>{item.title || piece?.title}</h2>
        {isHymn && piece && (piece.tune || piece.composer) && (
          <p className="italic text-text-muted text-sm mt-1">
            {[piece.tune && `Tune: ${piece.tune}`, piece.composer].filter(Boolean).join(' · ')}
          </p>
        )}
        <Rule />
        {item.hostOwnText && (
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-widest text-text-muted">Text supplied by the host</p>
            <p className="text-[10px] text-text-muted italic mb-2">The host is responsible for having the right to use this text.</p>
          </div>
        )}
        {(item.text) && (
          <p className={`${large ? 'text-2xl' : 'text-lg'} leading-relaxed whitespace-pre-line text-text-main`}>{item.text}</p>
        )}
        {isHymn && (
          <HymnBody item={item} large={large} />
        )}
      </div>
      {src && (
        <div className="mt-4"><AudioPlayer src={src} title={item.title || piece?.title} instrument={item.instrument} /></div>
      )}
    </Page>
  );
};

const Credits = ({ items, pieceMap, credits }) => {
  const seen = new Set();
  const lines = [];
  [...(Array.isArray(credits) ? credits : []), ...items.map(i => pieceMap[i.pieceId]?.credit)].forEach(c => {
    const text = typeof c === 'string' ? c : c?.text;
    if (text && !seen.has(text)) { seen.add(text); lines.push(text); }
  });
  return (
    <Page className="prog-credits">
      <div className="prog-serif text-center">
        <h2 className="text-xl font-bold text-accent-navy">Music Credits</h2>
        <Rule />
        {lines.length === 0 && <p className="text-sm text-text-muted">No music credits for this service.</p>}
        {lines.map(l => <p key={l} className="text-sm text-text-muted mb-2">{l}</p>)}
        <p className="text-[10px] uppercase tracking-widest text-text-muted mt-6">Prepared with OneFinalLegacy</p>
      </div>
    </Page>
  );
};

// Shared by the editor preview and the guest reader.
const BookletView = ({ programme, items, library = [], showAudio = false, large = false }) => {
  const pieceMap = {};
  library.forEach(p => { pieceMap[p.id] = p; });
  const ctx = { pieceMap, large, showAudio };
  const sorted = [...items].sort((a, b) => a.order - b.order);
  return (
    <div>
      <Cover programme={programme} />
      {sorted.map(i => <ItemPage key={i.id} item={i} ctx={ctx} />)}
      <Credits items={sorted} pieceMap={pieceMap} credits={programme.credits} />
    </div>
  );
};

export default BookletView;